import { Day } from '@amir04lm26/react-modern-calendar-date-picker'
import { DateTime } from 'luxon'

/**
 * Utility to convert a date string to a 'Day' format used by 'react-modern-calendar-datepicker'
 * @param dateStr
 * @param format i.e. https://moment.github.io/luxon/docs/manual/formatting.html#table-of-tokens
 * @returns the date as a 'Day'
 */
export const convertDateStringToDay = (
  dateStr: string,
  format?: string
): Day => {
  const { year, month, day } = format
    ? DateTime.fromFormat(dateStr, format)
    : DateTime.fromISO(dateStr)
  return {
    year,
    month,
    day
  }
}

/**
 * Utility to convert a 'Day' to a standard ISO date string minus time
 * @param day
 * @param month
 * @param year
 */
export const convertDayToISODateString = ({
  day,
  month,
  year
}: Day): string => {
  return DateTime.local(year, month, day).toISODate()
}

/**
 * Utility to convert a 'Day' to a Luxon DateTime format
 * @param day
 * @param month
 * @param year
 */
export const convertDayToDateTime = ({ day, month, year }: Day): DateTime => {
  return DateTime.local(year, month, day)
}

/**
 * Utility to compare two dates and return latest
 * @param firstDate
 * @param secondDate
 */
export const compareTwoDates = (
  firstDate: string,
  secondDate: string
): string => {
  return DateTime.fromISO(firstDate, { zone: 'utc' }) >
    DateTime.fromISO(secondDate, { zone: 'utc' })
    ? firstDate
    : secondDate
}

/**
 * Format a date string to a consistent display format (dd.MM.yyyy)
 * This ensures dates are formatted consistently across all environments (Windows, Linux, CI)
 * @param dateStr ISO date string
 * @param format Optional custom format (defaults to 'dd.MM.yyyy')
 * @returns Formatted date string
 * @example
 * formatDateForDisplay('1982-07-26') // '26.07.1982'
 * formatDateForDisplay('1982-07-26', 'dd/MM/yyyy') // '26/07/1982'
 * formatDateForDisplay('1982-07-26', 'MMM dd, yyyy') // 'Jul 26, 1982'
 */
export const formatDateForDisplay = (
  dateStr: string,
  format: string = 'dd.MM.yyyy'
): string => {
  return DateTime.fromISO(dateStr, { zone: 'utc' }).toFormat(format)
}

/**
 * Format a DateTime object to consistent display format
 * @param dateTime Luxon DateTime object
 * @param format Optional custom format (defaults to 'dd.MM.yyyy')
 * @returns Formatted date string
 * @example
 * formatDateTimeForDisplay(DateTime.now()) // '26.07.1982'
 */
export const formatDateTimeForDisplay = (
  dateTime: DateTime,
  format: string = 'dd.MM.yyyy'
): string => {
  return dateTime.toFormat(format)
}
